
import { defineComponent } from 'vue';
import { useStore } from 'vuex';
import { computed } from '@vue/runtime-core';
import { Actions, formFieldsStore, userSettingsStore, Getters } from '@/store';

import Utils from '@/utils/QSUtils';

import ModalHeader from '@/modules/modals/components/ModalHeader.vue';
import ModalFooter from '@/modules/modals/components/ModalFooter.vue';
import ModalBody from '@/modules/modals/components/ModalBody.vue';
import ModalService from '@/modules/modals/services/modalService';
import Loader from '@/components/Loader.vue';

import PreferredCasing from '@/modules/casing/components/PreferredCasing.vue';
import SourceOrder from '@/modules/sort-order/SourceOrder.vue';

import _ from 'lodash';
import LogUsageManager from '@/manager/LogUsageManager';
import theLocalStorageUtils from '@/utils/LocalStorageUtils';
import { LocalStorageKeys } from '@/constants/LocalStorageKeys';
import FormsManager from '@/manager/3.6/FormsManager';
import JWTUtils from '@/utils/JWTUtils';
import CompDataFeedsRequest from '@/store/3.6/models/CompDataFeedsRequest';

export default defineComponent({
    components: { ModalHeader, ModalBody, ModalFooter, PreferredCasing, SourceOrder, Loader },
    props: {
        defaultTabIdx: {
            default: 0,
            required: false,
            type: Number,
        },
    },
    data() {
        return {
            modal: null,
            sourceOrderCallback: {},
            usersUsingMySettings: [],
            isLoading: false,
        };
    },
    setup() {
        const store = useStore();
        const accessToken = theLocalStorageUtils.get(LocalStorageKeys.qsAccessToken);
        const activeUserPassportId = JWTUtils.getValue(accessToken ?? '', 'passportId');

        const computedSourceOrder = computed(() => userSettingsStore.DataSourceColumnOrder);
        const sourceOrder = _.cloneDeep(computedSourceOrder.value);

        const activeUser = {
            firstName: JWTUtils.getValue(accessToken ?? '', 'fn'),
            lastName: JWTUtils.getValue(accessToken ?? '', 'ln'),
            passportId: JWTUtils.getValue(accessToken ?? '', 'passportId'),
        };

        const computedSharedSettings = computed(() => userSettingsStore.SharedSettings);
        const sharedSettings = _.cloneDeep(computedSharedSettings.value);
        const useUserSettingsPassportId = sharedSettings.usingUserSettingsPassportId;
        const usersSharingSettings = sharedSettings.usersSharingSettings;
        const usersUsingMyShareSettings = sharedSettings.usersUsingMyShareSettings;

        return {
            store,
            sourceOrder,
            activeUser,
            activeUserPassportId,
            useUserSettingsPassportId,
            usersSharingSettings,
            usersUsingMyShareSettings,
        };
    },
    mounted() {
        const modalElement = this.$refs.configureSettings;
        this.modal = ModalService.initialize(modalElement, this);
        ModalService.size(modalElement, 'lg');
        ModalService.open(this.modal);

        this.filteredUsersUsingMySettings();
    },
    computed: {
        ExportAllComps: function () {
            const result = computed(() => userSettingsStore.ExportAllComps);
            return result.value;
        },
        SuggestDataPreferenceUpdate: function () {
            const result = computed(() => userSettingsStore.SuggestDataPreferenceUpdate);
            return result.value;
        },
        ShowDataPreferenceColumn: function () {
            const result = computed(() => userSettingsStore.DisplayDataCascadeColumn);
            return result.value;
        },
        PreferredCasing: function () {
            const result = computed(() => userSettingsStore.PreferredCasing);
            return result.value;
        },
        ShareMySettings: function () {
            const result = computed(() => userSettingsStore.ShareMySettings);
            return result.value;
        },
    },
    methods: {
        SaveGlobalSettings: async function () {
            const doNotSuggestDataPreferenceEl = document.getElementById('doNotSuggestDataPreference') as HTMLInputElement;
            const doNotSuggestDataPreference = doNotSuggestDataPreferenceEl.checked ? false : true;
            const originalDoNotSuggestDataPreferenceState = this.SuggestDataPreferenceUpdate;

            const exportAllComps = document.getElementById('exportAllComps') as HTMLInputElement;
            const originalExportAllCompsState = this.ExportAllComps;

            const preferredCasing = document.querySelector('input[name="casing"]:checked') as HTMLInputElement;
            const preferredCasingId = preferredCasing.dataset.id;
            const originalPreferredCasingState = this.PreferredCasing;

            const sourceOrder: any = this.sourceOrderCallback;

            const showDataPreferenceColumn = document.getElementById('showDataPreferenceColumn') as HTMLInputElement;
            const originalShowDataPreferenceColumnState = this.ShowDataPreferenceColumn;

            const shareMySettings = document.getElementById('shareMySettings') as HTMLInputElement;
            const originalShareMySettingsState = this.ShareMySettings;

            const useUserSettings = document.getElementById('useUserSettings') as HTMLSelectElement;
            const originalUseUserSettingsState = this.useUserSettingsPassportId;

            if (originalDoNotSuggestDataPreferenceState !== doNotSuggestDataPreference) {
                await userSettingsStore.setSuggestDataPreferenceUpdate({
                    SuggestDataPreferenceUpdate: doNotSuggestDataPreference,
                });
            }

            if (originalExportAllCompsState !== exportAllComps.checked) {
                await userSettingsStore.setExportAllComps({
                    ExportAllComps: exportAllComps.checked,
                });
                await LogUsageManager.sendAllPropertiesToTotal(exportAllComps.checked);
            }

            if (originalPreferredCasingState !== preferredCasing.value) {
                await userSettingsStore.setPreferredCasing({
                    PreferredCasing: preferredCasing.value,
                });

                const formCode = theLocalStorageUtils.get(LocalStorageKeys.formCode);
                await FormsManager.fetchAndStoreFormFields(formCode ?? '');

                const preferredCasingIdNumber = Number(preferredCasingId ?? 0);
                LogUsageManager.overrideCase(preferredCasingIdNumber);
            }

            if (Utils.objectSize(sourceOrder) > 0) {
                await userSettingsStore.setDataSourceColumnOrder({
                    DataSourceColumnOrder: sourceOrder,
                });
                await LogUsageManager.setCustomColumnOrder(sourceOrder);
            }

            if (originalShowDataPreferenceColumnState !== showDataPreferenceColumn.checked) {
                await userSettingsStore.setDisplayDataCascadeColumn({
                    DisplayDataCascadeColumn: showDataPreferenceColumn.checked,
                });

                let sourceOrderList = sourceOrder;
                // If sourceOrder was never changed then use the original sourceOrder
                if (Utils.objectSize(sourceOrder) === 0) {
                    sourceOrderList = this.sourceOrder;
                }

                // We do not allow the user to hide source columns but we do want to track the Data Preference Column.
                const showDataPreferenceColumnStatus = showDataPreferenceColumn.checked ? 1 : 0;
                LogUsageManager.alteredDataSourceVisibility(!sourceOrderList[0]['isHidden'], !sourceOrderList[1]['isHidden'], !sourceOrderList[2]['isHidden'], !sourceOrderList[3]['isHidden'], showDataPreferenceColumn.checked);
            }

            if (originalShareMySettingsState !== shareMySettings.checked) {
                await userSettingsStore.setShareMySettings({
                    ShareMySettings: shareMySettings.checked,
                });
            }

            if (originalUseUserSettingsState !== Number(useUserSettings.value)) {
                this.isLoading = true;

                await userSettingsStore.UseUserSettings({
                    passportId: useUserSettings.value,
                });

                await FormsManager.fetchAndStoreFormFields(theLocalStorageUtils.get(LocalStorageKeys.formCode) ?? '');
                await FormsManager.fetchAndStoreFormFieldsAdditionalDetails(theLocalStorageUtils.get(LocalStorageKeys.formCode) ?? '');
                await userSettingsStore.FetchSettings();
                //await FormsManager.fetchAndStoreAdditionalFields();

                const compsData = computed(() => this.store.getters[Getters.CompAddressesV36.Comps]);
                const comps = compsData.value;

                for (const idx in comps) {
                    const feedsRequest: CompDataFeedsRequest = {
                        address: comps[idx].address,
                        qsGuid: comps[idx].qsGuid,
                        formCode: theLocalStorageUtils.get(LocalStorageKeys.formCode) ?? '',
                        compsGuid: theLocalStorageUtils.get(LocalStorageKeys.almCompId) ?? '',
                        isSubject: false,
                        fetch: ['pr'],
                    };
                    await this.store.dispatch(Actions.CompAddressesV36.FetchCompDataFeeds, feedsRequest);
                }

                this.isLoading = false;
            }

            // ReRun Casing for all Tabs
            this.store.dispatch(Actions.CompAddressesV36.TransferRunFieldRowTransferValueCasingOnAllComps);

            ModalService.hide(this.modal);
        },
        isActiveTab(tabIdx: number) {
            return Number(this.defaultTabIdx) === tabIdx ? 'active' : '';
        },
        isActiveContent(tabIdx: number) {
            return Number(this.defaultTabIdx) === tabIdx ? 'active show' : '';
        },
        SourceOrderCallback(value: any) {
            this.sourceOrderCallback = value;
        },
        filteredUsersUsingMySettings(): void {
            //Filter out the Active User if they are sharing their settings
            this.usersUsingMySettings = this.usersUsingMyShareSettings.filter((item: { passportId: number }) => {
                return Number(item.passportId) !== Number(this.activeUserPassportId);
            });
        },
        getAvailableUserSettings() {
            const users = this.usersSharingSettings;
            const idx = this.usersSharingSettings.findIndex((x: { passportId: number }) => Number(x.passportId) === Number(this.activeUserPassportId));
            if (idx === -1) {
                users.push(this.activeUser);
            }
            return users;
        },
        isUsingUserSettings(usingPassportId: number): boolean {
            return Number(this.useUserSettingsPassportId) === Number(usingPassportId) ? true : false;
        },
        isShareMySettingsDisabled(): boolean {
            return this.usersUsingMySettings.length > 0 ? true : false;
        },
    },
});
